<template>
  <span class="pu-price">
    <span
      v-if="mainPriceObject"
      data-test="campsite-pricing"
      class="display-price"
    >
      {{ formatPrice(mainPriceObject) }}
    </span>
    <span
      v-if="displayPrice"
      v-show="shouldDisplayOriginalPrice"
      class="converted-price"
    >
      ({{ formatPrice(displayPrice) }})
    </span></span
  >
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useCurrencyStore } from '~/stores/useCurrencyStore'
import { formatPrice as fp } from '~/utility/formatCurrency'
import {
  MoneyRaw,
  MoneyType,
  priceToMoneyType,
  SimpleMoney,
} from '~/utility/money'

const { $i18n } = useNuxtApp()
const currencyStore = useCurrencyStore(usePinia())

const props = withDefaults(
  defineProps<{
    price: MoneyRaw | MoneyType | SimpleMoney
    displayOriginalPrice?: boolean
  }>(),
  { displayOriginalPrice: false },
)

onMounted(() => {
  if (
    displayPrice.value &&
    displayPrice.value.getCurrency() !== currencyStore.currentCurrencyCode
  ) {
    convertDisplayPriceCurrency()
  }
})

// watch can't be done on props, so we need to use computed
const price = computed(() => {
  return props.price
})

watch(price, () => {
  convertDisplayPriceCurrency()
})

const currentCurrency = computed(() => {
  return currencyStore.currentCurrencyCode
})

watch(currentCurrency, () => {
  convertDisplayPriceCurrency()
})

const currencyConvertedDisplayPrice = ref<MoneyRaw | null>(null)

function convertDisplayPriceCurrency() {
  if (priceMoney.value) {
    try {
      currencyConvertedDisplayPrice.value = currencyStore
        .convert(priceMoney.value, currencyStore.currentCurrencyCode)
        .toObject()
    } catch (error) {
      // On conversion error, fall back to original price
      console.error('Failed to convert currency in PuPrice:', error)
      currencyConvertedDisplayPrice.value = null
    }
  }
}

function formatPrice(priceObject: MoneyType) {
  return fp(priceObject, $i18n.locale)
}

const displayPrice = computed(() => {
  if (currencyConvertedDisplayPrice.value) {
    return priceToMoneyType(currencyConvertedDisplayPrice.value)
  }
  return priceMoney.value
})

const mainPriceObject = computed(() => {
  if (shouldDisplayOriginalPrice.value) {
    return priceMoney.value
  }
  return displayPrice.value
})

const priceMoney = computed(() => {
  return props.price ? priceToMoneyType(props.price) : null
})

const shouldDisplayOriginalPrice = computed(() => {
  if (props.displayOriginalPrice && priceMoney.value) {
    return currencyStore.currentCurrencyCode !== priceMoney.value.getCurrency()
  }
  return false
})
</script>

<style scoped>
.display-price {
  font-weight: bold;
  font-size: 1.125em;
  display: inline-block;
  min-width: max-content;
}

.converted-price {
  font-weight: normal;
}

.pu-price {
  white-space: normal;
}
</style>
